// src/components/dashboard/ChatPage.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const BOT_RESPONSE = "I'm not connected to your real data but to this if I were you would get all your information related to your data in this chat. This is a demo preview of my capabilities.";

export const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! I'm your personal health AI companion. Please note that I'm currently not connected to your real data but if I were, you would get all your information related to your data in this chat. How can I help you today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const getBotResponse = (_userMessage: string): string => {
    return BOT_RESPONSE;
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getBotResponse(inputValue),
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 60px)',
        backgroundColor: 'var(--color-background)',
        fontFamily: 'var(--font-primary)',
        width: '100%'
      }}
    >
      {/* DEMO WARNING BANNER */}
      <div style={{
        backgroundColor: '#FEF3C7',
        borderBottom: '2px solid #F59E0B',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexShrink: 0
      }}>
        <span style={{ fontSize: '20px' }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#92400E', marginBottom: '4px' }}>
            DEMO PREVIEW
          </div>
          <div style={{ fontSize: '13px', color: '#78350F' }}>
            This AI chat shows example responses only. Your real health data is not connected yet. Coming soon!
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--space-8)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)'
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)'
          }}
        >
          {messages.map((message) => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                animation: 'slideIn 0.3s ease-out'
              }}
            >
              <div
                style={{
                  maxWidth: '80%',
                  padding: 'var(--space-4) var(--space-5)',
                  borderRadius: message.sender === 'user'
                    ? '20px 20px 4px 20px'
                    : '20px 20px 20px 4px',
                  backgroundColor: message.sender === 'user'
                    ? 'var(--color-accent-blue)'
                    : 'var(--color-surface)',
                  color: message.sender === 'user'
                    ? '#ffffff'
                    : 'var(--color-text)',
                  fontSize: 'var(--text-lg)',
                  lineHeight: '1.6',
                  border: message.sender === 'bot'
                    ? '1px solid var(--color-border)'
                    : 'none',
                  boxShadow: message.sender === 'user'
                    ? '0 2px 8px var(--color-accent-blue-bg)'
                    : 'var(--shadow-sm)'
                }}
              >
                {message.text}
              </div>
            </div>
          ))}

          {isTyping && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start'
              }}
            >
              <div
                style={{
                  padding: 'var(--space-4) var(--space-5)',
                  borderRadius: '20px 20px 20px 4px',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center'
                }}
              >
                <span className="typing-dot" style={{ animationDelay: '0s' }}>●</span>
                <span className="typing-dot" style={{ animationDelay: '0.2s' }}>●</span>
                <span className="typing-dot" style={{ animationDelay: '0.4s' }}>●</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Container */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderTop: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          position: 'sticky',
          bottom: 0
        }}
      >
        {/* Quick Actions */}
        <div
          style={{
            marginBottom: 'var(--space-4)',
            display: 'flex',
            gap: 'var(--space-3)',
            flexWrap: 'wrap'
          }}
        >
          {['Cholesterol', 'Blood Pressure', 'Diet', 'Exercise', 'Vitamin D', 'Allergies'].map((topic) => (
            <button
              key={topic}
              onClick={() => setInputValue(topic)}
              style={{
                padding: '10px 20px',
                borderRadius: '24px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-background)',
                color: 'var(--color-secondary)',
                fontSize: 'var(--text-base)',
                cursor: 'pointer',
                transition: 'var(--transition-fast)',
                fontFamily: 'var(--font-primary)',
                fontWeight: 'var(--font-medium)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-accent-blue-bg)';
                e.currentTarget.style.borderColor = 'var(--color-accent-blue)';
                e.currentTarget.style.color = 'var(--color-accent-blue)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-background)';
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.color = 'var(--color-secondary)';
              }}
            >
              {topic}
            </button>
          ))}
        </div>

        {/* Input Area */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            alignItems: 'flex-end'
          }}
        >
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your health metrics..."
            rows={1}
            style={{
              flex: 1,
              padding: 'var(--space-4) var(--space-5)',
              borderRadius: '24px',
              border: '2px solid var(--color-border)',
              backgroundColor: 'var(--color-background)',
              color: 'var(--color-text)',
              fontSize: 'var(--text-lg)',
              fontFamily: 'var(--font-primary)',
              resize: 'none',
              outline: 'none',
              minHeight: '56px',
              maxHeight: '200px',
              transition: 'var(--transition-fast)'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-accent-blue)';
              e.currentTarget.style.boxShadow = '0 0 0 3px var(--color-accent-blue-bg)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: inputValue.trim()
                ? 'var(--color-accent-blue)'
                : 'var(--color-border)',
              color: '#ffffff',
              cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'var(--transition-fast)',
              flexShrink: 0,
              opacity: inputValue.trim() ? 1 : 0.5
            }}
            onMouseEnter={(e) => {
              if (inputValue.trim()) {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (inputValue.trim()) {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            <Send size={22} />
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes typingDot {
          0%, 60%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          30% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        .typing-dot {
          display: inline-block;
          font-size: 12px;
          color: var(--color-secondary);
          animation: typingDot 1.4s infinite;
        }

        @media (max-width: 768px) {
          /* Mobile responsive adjustments */
          div[style*="padding: var(--space-8)"] {
            padding: var(--space-4) !important;
          }
          div[style*="padding: var(--space-6)"] {
            padding: var(--space-4) !important;
          }
        }
      `}</style>
    </div>
  );
};
